import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, LocateFixed, MapPin, Sparkles } from "lucide-react";
import { catalogWebsocketUrl, getCatalog, processEvent } from "../api";
import type { Catalog, CatalogEvent, MapRecord } from "../types";
import { EventCatalog } from "./EventCatalog";
import { EventConfirmation } from "./Overlays";
import { CustomImport } from "./UploadView";

export function LandingPage({ onOpenMap }: { onOpenMap: (token: string) => void }) {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmEvent, setConfirmEvent] = useState<CatalogEvent | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState("");

  const refreshCatalog = useCallback(async () => {
    try { setCatalog(await getCatalog()); setError(""); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Unbekannter Fehler"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void refreshCatalog(); }, [refreshCatalog]);
  useEffect(() => {
    if (window.location.hash === "#termine") window.setTimeout(() => document.getElementById("termine")?.scrollIntoView({ block: "start" }), 0);
  }, []);
  useEffect(() => {
    const socket = new WebSocket(catalogWebsocketUrl());
    socket.onmessage = (message) => {
      const event = JSON.parse(message.data) as { type: string };
      if (event.type !== "heartbeat" && event.type !== "catalog.snapshot") void refreshCatalog();
    };
    const poll = window.setInterval(() => void refreshCatalog(), 60_000);
    return () => { socket.close(); window.clearInterval(poll); };
  }, [refreshCatalog]);

  function selectEvent(event: CatalogEvent) {
    if (event.map_token && ["available", "processing", "review"].includes(event.map_status)) {
      onOpenMap(event.map_token);
      return;
    }
    if (event.has_pdf) { setProcessError(""); setConfirmEvent(event); }
  }

  async function confirmProcessing() {
    if (!confirmEvent) return;
    setProcessing(true); setProcessError("");
    try {
      const response = await processEvent(confirmEvent.id, confirmEvent.map_status === "stale");
      onOpenMap(response.map.public_token);
    } catch (reason) { setProcessError(reason instanceof Error ? reason.message : "Verarbeitung konnte nicht gestartet werden"); }
    finally { setProcessing(false); }
  }

  function openCustom(map: MapRecord) { onOpenMap(map.public_token); }

  return (
    <main className="landing-page">
      <section className="landing-hero">
        <div className="hero-orb orb-one" /><div className="hero-orb orb-two" />
        <span className="eyebrow"><Sparkles size={14} /> Automatische Georeferenzierung</span>
        <h1>Dein Hofflohmarkt.<br /><em>Direkt auf der Karte.</em></h1>
        <p>Termin auswählen, Tourplan umwandeln und alle teilnehmenden Höfe als echte GPS-Punkte öffnen oder exportieren.</p>
        <a className="button button-primary hero-action" href="#termine">Termine entdecken <MapPin size={17} /></a>
      </section>
      <EventCatalog catalog={catalog} loading={loading} error={error} onEvent={selectEvent} />
      <CustomImport onCreated={openCustom} />
      <div className="trust-row landing-trust" aria-label="Vorteile">
        <span><CheckCircle2 size={17} /> Erkennt Höfe automatisch</span>
        <span><LocateFixed size={17} /> Echte GPS-Koordinaten</span>
        <span><MapPin size={17} /> Export für Google My Maps</span>
      </div>
      {confirmEvent && <EventConfirmation event={confirmEvent} busy={processing} error={processError} onClose={() => { if (!processing) setConfirmEvent(null); }} onOpenExisting={confirmEvent.map_status === "stale" && confirmEvent.map_token ? () => onOpenMap(confirmEvent.map_token!) : undefined} onConfirm={() => void confirmProcessing()} />}
    </main>
  );
}
